import { setBrowseBaseCategoryIds } from '@/redux/modules/browsePath/browsePath.reducer';
import { getCategoryEntries } from '@/redux/modules/category/category.selectors';
import { getCatalogIdsForCategory } from '@/redux/modules/categoryCatalogCards';
import { fetchCombinationCategory } from '@/redux/modules/combinationCategory';
import { getDeployment } from '@/redux/modules/config';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { getItemSummarySellerId, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import { searchCategory } from '@/redux/modules/landingPage';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { DEFAULT_SORT_ORDER } from '@/redux/modules/search/search.types';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { fetchSimilarItemsIfNeeded, getSimilarItemIds } from '@/redux/modules/similarItems';
import { FilterAction } from '@/types/SearchFacet';
import { mapBrowsePairsToSearchOptionIds } from '@/utils/browsePath/browsePath';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getPreservedRedirectParams } from '@/utils/queryParams';
import { getCategoryIdsFromPathname, getCategoryNameFromPathname, urlSafeTitle } from '@/utils/urls';

import { runPreloadPhase, runSearchPhase, runSecondaryPhase } from './categorySearchData/asyncPhases';
import { resolveBrowseInputs } from './categorySearchData/browseResolution';
import {
    buildCategorySearchQuery,
    CATEGORY_ID_BACKED_BROWSE_FACET_TYPES,
    DEFAULT_PRICE_GUIDE_SORT,
    mergeCategoryIdsIntoQueryParams,
} from './categorySearchData/queryBuilders';
import {
    getCanonicalBrowseRedirectUrl,
    getEarlyBrowseRedirectUrl,
    getLegacyQuadBrowseRedirectUrl,
} from './categorySearchData/redirects';
import { buildRouteContext } from './categorySearchData/routeContext';
import { CategoryEntryLite, CategoryLoaderParams } from './categorySearchData/types';

const getLandingPageIds = (routeContext: ReturnType<typeof buildRouteContext>, baseCategoryIds: number[]) => {
    if (routeContext.legacyPathParsed) {
        return [Number(routeContext.legacyPathParsed.numericCategoryId)];
    }
    if (routeContext.isBrowseSlugRoute) {
        return baseCategoryIds;
    }
    if (routeContext.isCombinationCategory) {
        return [routeContext.legacyCategoryId, routeContext.legacyCategoryId2];
    }
    if (routeContext.hasRegularLegacyCategory) {
        return [routeContext.legacyCategoryId];
    }
    return [];
};

export default ({ location, match, redirect, routerState, store }: CategoryLoaderParams) => {
    const { params } = match;
    const { dispatch: rawDispatch, getState } = store;
    const dispatch = (action: any) => (rawDispatch as any)(action);

    const routeContext = buildRouteContext({
        location: { pathname: location.pathname, search: location.search },
        params,
    });

    const earlyBrowseRedirectUrl = getEarlyBrowseRedirectUrl({
        browseParsed: routeContext.browseParsed,
        pathname: routeContext.pathname,
        queryParams: routeContext.queryParams,
    });
    if (earlyBrowseRedirectUrl) {
        return redirect(earlyBrowseRedirectUrl, true);
    }

    const canonicalBrowseRedirectUrl = getCanonicalBrowseRedirectUrl({
        locationSearch: routeContext.locationSearch,
        pathname: routeContext.pathname,
    });
    if (canonicalBrowseRedirectUrl) {
        return redirect(canonicalBrowseRedirectUrl, true);
    }

    const legacyQuadBrowseRedirectUrl = getLegacyQuadBrowseRedirectUrl({
        locationSearch: routeContext.locationSearch,
        pathname: routeContext.pathname,
    });
    if (legacyQuadBrowseRedirectUrl) {
        return redirect(legacyQuadBrowseRedirectUrl, true);
    }

    return runPreloadPhase({
        dispatch,
        isCombinationCategory: routeContext.isCombinationCategory,
        itemId: routeContext.itemId,
        legacyCategoryId2: routeContext.legacyCategoryId2,
        params,
        pathname: routeContext.pathname,
        queryParams: routeContext.queryParams,
    }).then(async ({ combinationIds }) => {
        const stateAfterFacets = getState();
        const deployment = String(getDeployment(stateAfterFacets) || '');

        const browseResolution = await resolveBrowseInputs({
            deployment,
            locationSearch: routeContext.locationSearch,
            routeContext,
            searchFacets: getSearchFacets(stateAfterFacets),
            stateAfterFacets,
        });
        if (browseResolution.redirectUrl) {
            return redirect(browseResolution.redirectUrl, true);
        }

        const {
            baseCategoryIds,
            browseFacetOptionIds,
            browsePathPairsForPostSearchRemap,
            shouldAttemptPostSearchBrowseRemap,
            slugOnlyPathForInvalidBrowsePath,
            unresolvedBrowseSlugForKeywordSearch,
        } = browseResolution;

        const browseSlugForState =
            routeContext.isBrowseSlugRoute && params.browseCategorySlug ? params.browseCategorySlug : null;
        dispatch(setBrowseBaseCategoryIds({ ids: baseCategoryIds, browseCategorySlug: browseSlugForState }));

        const finalCategoryIdForTiles = baseCategoryIds.length ? baseCategoryIds[baseCategoryIds.length - 1] : 0;
        const adsCategoryKey = baseCategoryIds.length
            ? baseCategoryIds.join(',')
            : getCategoryIdsFromPathname(routeContext.pathname);
        await runSecondaryPhase({
            adsCategoryKey,
            dispatch,
            finalCategoryIdForTiles,
            itemId: routeContext.itemId,
            keywords: routeContext.keywords,
            pageName: routeContext.pageName,
            pathname: routeContext.pathname,
            relatedSearchTilesParams: routeContext.relatedSearchTilesParams,
            searchTerm:
                params.browseCategorySlug?.replace(/-/g, ' ') || getCategoryNameFromPathname(routeContext.pathname),
            shouldFetchCategoryBanner: baseCategoryIds.length > 0,
        });

        const currentState = getState();
        const defaultSortOrder = routeContext.archivedSearch ? DEFAULT_PRICE_GUIDE_SORT : DEFAULT_SORT_ORDER;
        const pageSize = getSearchPageSize(currentState);
        const catalogIds = getCatalogIdsForCategory(currentState);
        const rawFacets = getSearchFacets(currentState);
        // Sub-category browse slugs (e.g. /c/earrings/...) aren't in the top-cat map and may not be
        // resolved by category-api. Falling back to a keyword search keeps the canonical URL useful
        // instead of rendering an empty Oops page.
        const queryParamsForSearchInputs =
            unresolvedBrowseSlugForKeywordSearch && !routeContext.queryParams.keyword
                ? {
                      ...routeContext.queryParams,
                      keyword: unresolvedBrowseSlugForKeywordSearch.replace(/-/g, ' '),
                  }
                : routeContext.queryParams;
        const { queryParamsForSearch, searchQuery } = buildCategorySearchQuery({
            browseFacetOptionIds,
            defaultSortOrder,
            pathname: routeContext.pathname,
            queryParams: queryParamsForSearchInputs,
            rawFacets,
            searchPageSize: pageSize,
        });

        const landingPageIds = getLandingPageIds(routeContext, baseCategoryIds);
        const nonFilterActions: FilterAction[] = [
            FilterAction.PageChanged,
            FilterAction.PageSizeChanged,
            FilterAction.SortByChanged,
            FilterAction.ViewPriceResults,
            FilterAction.ViewUpcomingItems,
        ];
        const isSSR = !routerState;
        const isFilterRefinement =
            Boolean(routerState?.filterAction) && !nonFilterActions.includes(routerState?.filterAction);
        const needsAggs = isSSR || isFilterRefinement || shouldAttemptPostSearchBrowseRemap;

        // When the slug couldn't be resolved to category ids and we injected a keyword,
        // route through the generic keyword search; categoryweb rejects empty `ids` arrays.
        const useKeywordSearchFallback = Boolean(unresolvedBrowseSlugForKeywordSearch) && landingPageIds.length === 0;

        await runSearchPhase({
            catalogIds,
            dispatch,
            landingPageIds,
            needsAggs,
            searchQuery,
            useKeywordSearchFallback,
        });

        let searchState = getState();
        if (shouldAttemptPostSearchBrowseRemap && browsePathPairsForPostSearchRemap.length > 0) {
            const postSearchFacets = getSearchFacets(searchState);
            const remappedBrowseFacetOptionIds = mapBrowsePairsToSearchOptionIds(
                browsePathPairsForPostSearchRemap,
                postSearchFacets
            );
            const remappedHasAnyFacetIds = Object.values(remappedBrowseFacetOptionIds).some(
                (ids) => Array.isArray(ids) && ids.length > 0
            );

            if (remappedHasAnyFacetIds && !useKeywordSearchFallback) {
                /* Re-dispatch with the resolved facet ids; only safe when we have category ids */
                const remappedCategoryIdValues = Object.entries(remappedBrowseFacetOptionIds).flatMap(
                    ([facetType, ids]) => (CATEGORY_ID_BACKED_BROWSE_FACET_TYPES.has(facetType) ? ids : [])
                );
                const rerunQueryParams = mergeCategoryIdsIntoQueryParams(
                    queryParamsForSearch,
                    remappedCategoryIdValues
                );
                const { searchQuery: remappedSearchQuery } = buildCategorySearchQuery({
                    browseFacetOptionIds: remappedBrowseFacetOptionIds,
                    defaultSortOrder,
                    pathname: routeContext.pathname,
                    queryParams: rerunQueryParams,
                    rawFacets: postSearchFacets,
                    searchPageSize: pageSize,
                });

                await dispatch(
                    searchCategory({
                        ids: landingPageIds,
                        pageName: null,
                        searchQuery: remappedSearchQuery,
                        skipAggs: !needsAggs,
                    })
                );
                searchState = getState();
            } else if (!remappedHasAnyFacetIds && slugOnlyPathForInvalidBrowsePath) {
                /* Path's facet pairs couldn't be validated against post-search facets — strip to
                 * slug-only so the URL reflects what we actually have results for. */
                const querySuffix =
                    routeContext.locationSearch && routeContext.locationSearch !== '?'
                        ? routeContext.locationSearch
                        : '';
                return redirect(`${slugOnlyPathForInvalidBrowsePath}${querySuffix}`, true);
            }
        }

        const itemIds = getSearchedItemIds(searchState);
        const followUpPromises: Promise<any>[] = [];
        const preservedParams = getPreservedRedirectParams(routeContext.queryParams);

        if (itemIds.length === 0 && routeContext.queryParams?.page > 0) {
            redirect?.(`${location.pathname}${preservedParams}`, true);
            return;
        }

        if (itemIds.length === 0 && combinationIds) {
            const category = (Object.values(getCategoryEntries(searchState)) as CategoryEntryLite[])
                .filter((entry) => entry.facetId === 1)
                .pop();

            if (category?.name && category?.categoryId) {
                redirect?.(`/c/${urlSafeTitle(category.name)}/${category.categoryId}${preservedParams}`, true);
                return;
            }
        }

        if (combinationIds) {
            followUpPromises.push(dispatch(fetchCombinationCategory(combinationIds)));
        }

        if (itemIds?.length) {
            if (params?.deleted && !Number.isNaN(params.deleted)) {
                followUpPromises.push(
                    dispatch(
                        fetchItemSummariesIfNeeded({
                            identifier: 'MY-SAVED-SEARCH-PAGE-DELETED',
                            itemIds: [params.deleted],
                        })
                    )
                );
            }

            await Promise.all(followUpPromises);
            return;
        }

        const { soldItemIds = [] } = getSearchUiState(searchState);
        followUpPromises.push(
            dispatch(
                fetchItemSummariesIfNeeded({
                    identifier: 'MY-SAVED-SEARCH-PAGE-SOLD',
                    itemIds: soldItemIds,
                })
            )
        );
        followUpPromises.push(dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])));

        await Promise.all(followUpPromises);

        const stateAfterSold = getState();
        const soldItemId = soldItemIds[0];
        const sellerId = getItemSummarySellerId(stateAfterSold, soldItemId);
        const title = getItemSummaryTitle(stateAfterSold, soldItemId);
        const facets = getItemFacets(stateAfterSold, soldItemId);
        const categoryIds = getFacetIds(facets);

        await Promise.all([
            dispatch(
                fetchSimilarItemsIfNeeded({
                    categoryIds,
                    itemId: soldItemId,
                    sellerId,
                    title,
                })
            ),
        ]);

        const stateAfterSimilar = getState();
        const similarItemIds = getSimilarItemIds(stateAfterSimilar, soldItemId);
        await dispatch(
            fetchItemSummariesIfNeeded({
                identifier: 'MY-SAVED-SEARCH-PAGE-SIMILAR',
                itemIds: similarItemIds,
            })
        );
    });
};
